/**
 * Product catalog utilities — per-currency pricing plans.
 * Display price + productId are currency-specific so the price shown equals
 * the price charged. /api/payments/create resolves the productId (product_key)
 * in the products table, so the DB must contain rows for each currency variant
 * (e.g. basic-month-jpy, basic-month-krw). Free plan has no price / productId.
 */

import { supabase } from '@/lib/supabase'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'

export type Currency = 'AED'

// Live products are AED-denominated (single currency). Display == charge.
// Multi-currency (JPY/KRW/USD) is deferred until those product rows exist in
// the DB + the N-Genius outlet enables them (see migration 20260530120000).
export const LOCALE_CURRENCY: Record<LocaleCode, Currency> = {
  en: 'AED',
  ko: 'AED',
  ja: 'AED',
  zh: 'AED',
  'zh-TW': 'AED',
}

export interface PricingPlan {
  title: string
  planType: string
  monthlyPrice?: string
  monthlyPriceUnit?: string
  monthlyProductId?: string
  yearlyPrice?: string
  /** 12x the monthly price (the "before" price for the annual plan), formatted. */
  yearlyOriginalPrice?: string
  /** Amount saved per year vs paying 12 monthly installments, formatted (e.g. "AED 183.50"). */
  yearlySavings?: string
  /** Saving as a whole-number percent off the 12x-monthly price (e.g. 17). */
  yearlySavingsPct?: number
  yearlyPriceUnit?: string
  yearlyProductId?: string
  oneTimePrice?: string
  oneTimePriceUnit?: string
  oneTimeProductId?: string
  description: string
  features: string[]
  isHighlighted?: boolean
  badge?: string
  isDisabled?: boolean
}

// Per-currency display price + product_key for the paid plans. Fixed local
// pricing (not FX-converted). The product_key variants must exist in the DB.
interface PaidTier {
  m: string // monthly display price
  y: string // yearly display price
  mId: string // monthly product_key
  yId: string // yearly product_key
}
// AED prices match the live products table EXACTLY (shown == charged):
// basic-month 91.75 · basic-year 917.50 · business-month 440.40 · business-year 4404.00.
const PAID_PRICING: Record<Currency, { creator: PaidTier; business: PaidTier }> = {
  AED: {
    creator: { m: 'AED 91.75', y: 'AED 917.50', mId: 'basic-month', yId: 'basic-year' },
    business: { m: 'AED 440.40', y: 'AED 4,404.00', mId: 'business-month', yId: 'business-year' },
  },
}

// Single source for marketing teasers (PricingSection on /compare, legacy
// homepage) so they can never drift back to stale USD like the pre-fix state.
// Derived from PAID_PRICING above; Free is AED 0 (mirrors PricingPage).
export const MONTHLY_DISPLAY_PRICE_AED: Record<'free' | 'creator' | 'business', string> = {
  free: 'AED 0',
  creator: PAID_PRICING.AED.creator.m,
  business: PAID_PRICING.AED.business.m,
}

// Canonical EN feature lists — the localized copies live in the i18n dict
// (pricingPage.plans). fetchAndProcessProducts and EN teasers both read these,
// so the catalog has one source of truth.
export const PLAN_FEATURES_EN: Record<'free' | 'creator' | 'business', readonly string[]> = {
  free: ['1 Live App', '500 AI Credits / month', '2% Transaction Fee', 'Standard Support'],
  creator: ['5 Live Apps', '5,000 AI Credits / month', '1.5% Transaction Fee', 'Custom Domain Support', 'Basic Analytics'],
  business: ['30 Live Apps', '20,000 AI Credits / month', '1.0% Transaction Fee', 'Advanced Analytics', 'Priority Support'],
}

const FREE_PLAN: PricingPlan = {
  title: 'Free',
  planType: 'free',
  description: 'Best Suited For: Creators validating their first product idea.',
  features: [...PLAN_FEATURES_EN.free],
}

// Annual billing is 10x the monthly price (2 months free), so the yearly plan
// saves 2 monthly installments vs paying month-to-month. These helpers derive
// the "before" price + saving from the display strings so the price math stays
// in one place (the catalog), not the view. Currency-prefix-agnostic.
function priceCurrencyPrefix(price: string): string {
  return price.replace(/[\d.,\s]/g, '').trim() // "AED 91.75" -> "AED"
}
function priceToNumber(price: string): number {
  return parseFloat(price.replace(/[^\d.]/g, '')) // "AED 4,404.00" -> 4404
}
function formatPrice(prefix: string, n: number): string {
  return `${prefix} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
/** Adds yearlyOriginalPrice (12x monthly), yearlySavings, and yearlySavingsPct. */
function withAnnualSavings(plan: PricingPlan): PricingPlan {
  if (!plan.monthlyPrice || !plan.yearlyPrice) return plan
  const prefix = priceCurrencyPrefix(plan.monthlyPrice)
  const monthly = priceToNumber(plan.monthlyPrice)
  const yearly = priceToNumber(plan.yearlyPrice)
  if (!Number.isFinite(monthly) || !Number.isFinite(yearly)) return plan
  const original = monthly * 12
  const savings = original - yearly
  if (!(savings > 0)) return plan // no discount -> no savings UI
  return {
    ...plan,
    yearlyOriginalPrice: formatPrice(prefix, original),
    yearlySavings: formatPrice(prefix, savings),
    yearlySavingsPct: Math.round((savings / original) * 100),
  }
}

export async function fetchAndProcessProducts(currency: Currency = 'AED'): Promise<PricingPlan[]> {
  const p = PAID_PRICING[currency] ?? PAID_PRICING.AED

  const creator: PricingPlan = {
    title: 'Creator',
    planType: 'creator',
    monthlyPrice: p.creator.m,
    monthlyPriceUnit: '/ month',
    yearlyPrice: p.creator.y,
    yearlyPriceUnit: '/ year',
    monthlyProductId: p.creator.mId,
    yearlyProductId: p.creator.yId,
    isHighlighted: true,
    badge: 'Most Popular',
    description: 'Best Suited For: Influencers launching multiple campaigns.',
    features: [...PLAN_FEATURES_EN.creator],
  }

  const business: PricingPlan = {
    title: 'Business',
    planType: 'business',
    monthlyPrice: p.business.m,
    monthlyPriceUnit: '/ month',
    yearlyPrice: p.business.y,
    yearlyPriceUnit: '/ year',
    monthlyProductId: p.business.mId,
    yearlyProductId: p.business.yId,
    isHighlighted: false,
    description: 'Best Suited For: High-volume Proxy Buyers and Creator Brands.',
    features: [...PLAN_FEATURES_EN.business],
  }

  return [FREE_PLAN, withAnnualSavings(creator), withAnnualSavings(business)]
}

// Keep supabase import reachable for a future DB-backed catalog without churn.
void supabase
